// src/components/ProductList.tsx
import React, { useState, useMemo } from 'react';
import { Button, Badge, Alert, Row, Col } from 'react-bootstrap';
import { useCanvaAuth } from '../contexts/CanvaAuthContext2.tsx';
import DowngradeConfirmationModal  from './DowngradeConfirmationModal.js';

type ProductListProps = {
  billingPeriod?: 'monthly' | 'annual';
};

const ProductList: React.FC<ProductListProps> = ({ billingPeriod = 'monthly' }) => {
  const { canvaAuthenticated, setCanvaAuthenticated, token } = useCanvaAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | false>(false);
  const [downgradeFlyout, setDowngradeFlyout] = useState<any>(false);
  
  // Helper function to convert tier to snake_case (e.g., 1.0 -> "1_0", 2.5 -> "2_5")
  const tierToSnakeCase = (tier: string | number): string => {
    const tierNum = parseFloat(String(tier));
    // Use toFixed(1) to ensure one decimal place, then remove trailing zero if it's a whole number
    // But keep the decimal format (e.g., 1.0 stays as "1.0", not "1")
    const tierStr = tierNum % 1 === 0 ? tierNum.toFixed(1) : tierNum.toString();
    // Replace dot with underscore
    return tierStr.replace('.', '_');
  };

  // Filter products based on billing period (only for tier >= 2.0, tier < 2.0 always shown)
  const filteredProducts = useMemo(() => {
    if (!canvaAuthenticated) {
      return [];
    }
    
    const userProfile = canvaAuthenticated.userProfile;
    const isPricingTest = userProfile.pricingTest === true;
    
    if (!isPricingTest) {
      return userProfile.products;
    }
    
    return userProfile.products.filter((product: any) => {
      const productTier = parseFloat(product.tier);
      // Always show products with tier < 2.0
      if (productTier < 2.0) {
        return true;
      }
      // For tier >= 2.0, filter based on billing period
      // billing field should be 'month' for monthly or 'year' for annual
      if (billingPeriod === 'monthly') {
        return product.billing === 'month';
      } else if (billingPeriod === 'annual') {
        return product.billing === 'year';
      }
      return false;
    });
  }, [canvaAuthenticated, billingPeriod]);
  
  if (!canvaAuthenticated) {
    return <div>Loading...</div>;
  }
  
  const userProfile = canvaAuthenticated.userProfile;
  const env = canvaAuthenticated.env;
  const isPricingTest = userProfile.pricingTest === true;

  const onCancel = () => {
    // Navigation handled elsewhere
  };

const handleProductAction = async (product) => {

		setIsLoading(true);
		const priceId=product.stripePriceId;
		const keyTypeId=product.apiKeyTypeId;
		const productId=product.id;
		  const subscriptionId=userProfile.activeProduct.subscriptionId;
		  
		  // Determine if this is an upgrade or downgrade
		  const productTier = parseFloat(product.tier);
		  const activeTier = parseFloat(userProfile.activeProduct.tier);
		  const isTierUpgrade = productTier > activeTier;
		  
		  // If tiers are equal, check billing period: monthly -> annual is an upgrade
		  const isBillingUpgrade = productTier === activeTier && 
		    userProfile.activeProduct.billing === 'month' && 
		    product.billing === 'year';
		  
		  const isUpgrade = !subscriptionId || isTierUpgrade || isBillingUpgrade;
		  
		  if (isUpgrade) {
			// New purchase or subscription upgrade
			
			try {
				let url = env.stripeSessionUrl;
			  const response = await fetch(url, {
				method: 'POST',
				headers: {
				  'Authorization': `Bearer ${token}`,
				  'Content-Type': 'application/json',
				},
				body: JSON.stringify({ priceId, productId,keyTypeId, subscriptionId }),
			  });
		
			  const session = await response.json();
			
			  if (session.url) {
				window.location.href=session.url;

			  } else {
				setError(env.strings['app-error-session-expired'] || 'app-error-session-expired');
			  }
			} catch (error: any) {
			  setError(`Failed: ${error.message}`);
			} finally {
			 // setLoadingButtons(false); // Clear loading state for all buttons
			}
		  } else {
			// Update existing subscription (downgrade)
			try {
		
				let url = env.stripeDowngradeUrl;
			  const response = await fetch(url, {
				method: 'POST',
				headers: {
				  'Authorization': `Bearer ${token}`,
				  'Content-Type': 'application/json',
				},
				body: JSON.stringify({ priceId, productId,keyTypeId,subscriptionId }),
			  });
		
			  const result = await response.json();
			  if (result.error) {
				setError('Error updating subscription');
			  } else {
				window.close();
			  }
			} catch (error: any) {
			  	setError(`Failed to downgrade: ${error.message}`);
			} finally {
			 
			} 
		  }
		};

  

  const handleDowngradeConfirmation = (product) => {
    handleProductAction(product);
    setDowngradeFlyout(false);
  };

  const handleDowngradeClick = (product) => {
    // Handle downgrade click (if needed)
  };

  const getButton = (product) => {
  
    // Determine button properties dynamically
    let onClick: React.MouseEventHandler<HTMLButtonElement>;
    let variant: string = 'primary';
    let disabled: boolean = isLoading || userProfile.activeProduct.expires;
    let label: React.ReactNode = isLoading ?  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span> : (env.strings['app-product-list-button-label-upgrade'] || 'app-product-list-button-label-upgrade');
  
    const productTier = parseFloat(product.tier);
    const activeTier = parseFloat(userProfile.activeProduct.tier);
    const isFreePlan = product.price === "free" || productTier <= 1.0;
  
    if (product.id === userProfile.activeProduct.id) {
      // Your plan - check expires first, then renewDate
      disabled = true;
      
      // For tier 1.0 products, use outline-secondary (tertiary) instead of primary/warning
      if (Math.abs(productTier - 1.0) < 0.01) {
        variant = 'outline-secondary';
        // Still show renewal/expiration info if available
        if (userProfile.activeProduct.expires) {
          const expiresDate = userProfile.activeProduct.expires.includes('T') 
            ? userProfile.activeProduct.expires.split('T')[0] 
            : userProfile.activeProduct.expires;
          label = (
            <>
              {env.strings['app-text-subscription-ends'] || 'app-text-subscription-ends'} {expiresDate}
            </>
          );
        } else if (userProfile.renewDate) {
          const renewDate = userProfile.renewDate.includes('T') 
            ? userProfile.renewDate.split('T')[0] 
            : userProfile.renewDate;
          label = (
            <>
              {env.strings['app-text-subscription-renews'] || 'app-text-subscription-renews'} {renewDate}
            </>
          );
        } else {
          label = env.strings['app-product-list-button-label-your-plan'] || 'app-product-list-button-label-your-plan';
        }
      } else if (Math.abs(productTier - 1.9) < 0.01) {
        // For tier 1.9 products (not renewable), use subscription-ends instead of subscription-renews
        // Check expires first - use warning/danger color
        if (userProfile.activeProduct.expires) {
          variant = 'warning';
          const expiresDate = userProfile.activeProduct.expires.includes('T') 
            ? userProfile.activeProduct.expires.split('T')[0] 
            : userProfile.activeProduct.expires;
          label = (
            <>
              {env.strings['app-text-subscription-ends'] || 'app-text-subscription-ends'} {expiresDate}
            </>
          );
        } else if (userProfile.renewDate) {
          // For tier 1.9, use subscription-ends instead of subscription-renews
          variant = 'primary';
          const renewDate = userProfile.renewDate.includes('T') 
            ? userProfile.renewDate.split('T')[0] 
            : userProfile.renewDate;
          label = (
            <>
              {env.strings['app-text-subscription-ends'] || 'app-text-subscription-ends'} {renewDate}
            </>
          );
        } else {
          // Default - no renewal/expiration info
          variant = 'primary';
          label = env.strings['app-product-list-button-label-your-plan'] || 'app-product-list-button-label-your-plan';
        }
      } else {
        // Check expires first - use warning/danger color
        if (userProfile.activeProduct.expires) {
          variant = 'warning';
          const expiresDate = userProfile.activeProduct.expires.includes('T') 
            ? userProfile.activeProduct.expires.split('T')[0] 
            : userProfile.activeProduct.expires;
          label = (
            <>
              {env.strings['app-text-subscription-ends'] || 'app-text-subscription-ends'} {expiresDate}
            </>
          );
        } else if (userProfile.renewDate) {
          // Renewal - use primary/blue color
          variant = 'primary';
          const renewDate = userProfile.renewDate.includes('T') 
            ? userProfile.renewDate.split('T')[0] 
            : userProfile.renewDate;
          label = (
            <>
              {env.strings['app-text-subscription-renews'] || 'app-text-subscription-renews'} {renewDate}
            </>
          );
        } else {
          // Default - no renewal/expiration info
          variant = 'primary';
          label = env.strings['app-product-list-button-label-your-plan'] || 'app-product-list-button-label-your-plan';
        }
      }
      
      onClick = () => {}; // No-op for current plan
    } else if (productTier < activeTier || (productTier === activeTier && userProfile.activeProduct.billing === 'year' && product.billing === 'month')) {
      // Downgrade option - button disabled if active tier < 2.0
      // Also treat same-tier annual -> monthly as downgrade
      variant = 'outline-secondary'; // Tertiary style (outline-secondary in Bootstrap)
      disabled = activeTier < 2.0 || disabled;
      onClick = activeTier >= 2.0 ? () => { setDowngradeFlyout(product); } : () => {};
      label = isLoading ? (env.strings['app-product-list-button-label-loading'] || 'app-product-list-button-label-loading') : (env.strings['app-product-list-button-label-downgrade'] || 'app-product-list-button-label-downgrade');
    } else if (isFreePlan && activeTier <= 1.0) {
      // If user is on free plan and looking at free plan (different product), disable it
      variant = 'secondary';
      disabled = true;
      label = (env.strings['app-product-list-button-label-your-plan'] || 'app-product-list-button-label-your-plan');
      onClick = () => {}; // No-op for free plan when already on free
    } else {
      // For tier 1.0 products, use outline-secondary (tertiary) instead of primary
      if (Math.abs(productTier - 1.0) < 0.01) {
        variant = 'outline-secondary';
      }
      onClick = () => { handleProductAction(product); };
    }
  
    // Render the button
    const buttonClassName = product.id === userProfile.activeProduct.id 
      ? "rounded-4 product-btn btn-your-plan" 
      : "rounded-4 product-btn";
    
    return (
      <Button 
        className={buttonClassName}
        variant={variant}
        onClick={onClick}
        disabled={disabled}
      >
        {label}
      </Button>
    );
  };


  const renderContent = () => {
    return (
      	<Row className="text-center g-3 justify-content-center">
      		{error && <Col md={12}><Alert variant="danger">{error}</Alert></Col>}
          {
          filteredProducts.map((product, index) => {
            const tierSnakeCase = tierToSnakeCase(product.tier);
            const headlineStringKey = `app-product-list-tier_${tierSnakeCase}-headline`;
            const footerStringKey = `app-product-list-tier_${tierSnakeCase}-footer`;
            
            const productTierForCard = parseFloat(product.tier);
            const isTier1_0 = productTierForCard === 1.0;
            const cardClasses = [
              'product-card',
              'd-flex',
              'flex-column',
              'h-100',
              'border',
              'shadow',
              'rounded-4',
              userProfile.activeProduct.id === product.id ? 'product-card-your-plan' : 'bg-white',
              isTier1_0 ? 'product-card-tier-1-0' : ''
            ].filter(Boolean).join(' ');
            
            return (
              <Col lg={3} md={6} xs={12} key={product.id || `product-${index}`}>
                <div className={cardClasses}>
                  {/* Badge for active subscription - "Your plan" indicator */}
                  {userProfile.activeProduct.id === product.id && (
                    <Badge className="position-absolute start-0 translate-middle-y mt-2 ms-2" bg="info">
                      {env.strings['app-product-list-button-label-your-plan'] || 'app-product-list-button-label-your-plan'}
                    </Badge>
                  )}
              
                  {/* Product Name */}
                  <h4 className="product-name">{product.name || 'app-product-name-unknown'}</h4>
                  
                  {/* Product Headline - only if pricingTest is true */}
                  {isPricingTest && (
                    <div className="product-headline text-muted" style={{ whiteSpace: 'pre-line' }}>
                      {env.strings[headlineStringKey] || ''}
                    </div>
                  )}
              
                  {/* Product Price and Currency */}
                  <div className="product-price">
                    <h1 className="fw-semibold">
                      {product.price=="free" ? (env.strings['app-price-free'] || 'app-price-free') : product.price}{product.currency ? '\u00A0'+product.currency : ''}
                    </h1>
                    <div className="mt-n1">
                      {(() => {
                        const productTier = parseFloat(product.tier);
                        
                        // Tier 1.9 uses specific label
                        if (productTier === 1.9) {
                          return env.strings['app-product-list-price-pay-once'] || 'app-product-list-price-pay-once';
                        }
                        
                        // Default logic for other tiers
                        if (productTier == 1.0) {
                          return env.strings['app-product-list-price-each-month'] || 'app-product-list-price-each-month';
                        } else {
                          return product.billing === 'year' 
                            ? (env.strings['app-product-list-price-per-year'] || 'app-product-list-price-per-year')
                            : (env.strings['app-product-list-price-per-month'] || 'app-product-list-price-per-month');
                        }
                      })()}
                    </div>
                  </div>
              
                  {/* Product Features */}
                  <div className="product-features flex-grow-1">
                    {product.features.map((feature, featureIndex) => {
                      const stringKey = `product-feature-${feature.featureKey}-description`;
                      const isBold = featureIndex < 2;
                      return (
                        <div key={`feature-${featureIndex}`} className="feature-item d-flex">
                          <span className="me-2 flex-shrink-0">&#9702;</span>
                          <span className={isBold ? 'fw-bold' : ''} style={{ flex: 1, textAlign: 'left' }}>
                            {env.strings[stringKey] || stringKey}
                          </span>
                        </div>
                      );
                    })}
                  </div>
              
                  {/* Product Button */}
                  <div className="mt-auto">
                    {getButton(product)}
                  </div>
                  
                  {/* Product Footer - only if pricingTest is true */}
                  {isPricingTest && env.strings[footerStringKey] && (
                    <div className="product-footer mt-2" style={{ fontStyle: 'italic' }}>
                      {env.strings[footerStringKey]}
                    </div>
                  )}
                </div>
              </Col>
            );
          })
          }
          <DowngradeConfirmationModal
			show={downgradeFlyout}
			product={downgradeFlyout}
			onHide={() => setDowngradeFlyout(false)}
			onConfirm={handleDowngradeConfirmation}
		  />
      </Row>
    );
  };


  return <>{renderContent()}</>;
};

export default ProductList;
